import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuote, sendQuote, deleteQuote, duplicateQuote, clearSendStatus } from "../../store/slices/quotesSlice";
import { formatDate, formatCurrency, getStatusColor, formatRelativeDate } from "../../utils/format";
import {
  PaperAirplaneIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  LinkIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentQuote: quote } = useSelector((state) => state.quotes);
  const { sendStatus } = useSelector((state) => state.quotes);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dispatch(fetchQuote(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (sendStatus === "succeeded") {
      dispatch(clearSendStatus());
      dispatch(fetchQuote(id));
    }
  }, [sendStatus, dispatch, id]);

  if (!quote) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  const handleSend = () => {
    if (window.confirm("Send this quote to the client via email?")) {
      dispatch(sendQuote(id));
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this quote?")) {
      dispatch(deleteQuote(id));
      navigate("/quotes");
    }
  };

  const handleDuplicate = async () => {
    const result = await dispatch(duplicateQuote(id));
    if (result.payload?._id) {
      navigate(`/quotes/${result.payload._id}`);
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/q/${quote.shareToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lowestPrice = Math.min(...quote.cabinSelections.map((c) => c.pricePerPerson));
  const currency = quote.cabinSelections[0]?.currency || "CAD";

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link to="/quotes" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2">
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Quotes
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            Quote for {quote.client.firstName} {quote.client.lastName}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(quote.status)}`}>
              {quote.status}
            </span>
            {quote.viewCount > 0 && (
              <span className="flex items-center text-sm text-gray-500">
                <EyeIcon className="h-4 w-4 mr-1" /> Viewed {quote.viewCount} time{quote.viewCount !== 1 ? "s" : ""}
              </span>
            )}
            {quote.lastViewedAt && (
              <span className="text-sm text-gray-400">
                Last viewed {formatRelativeDate(quote.lastViewedAt)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {(quote.status === "draft" || quote.status === "viewed") && (
            <button
              onClick={handleSend}
              disabled={sendStatus === "loading"}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-4 w-4 mr-2" />
              {sendStatus === "loading" ? "Sending..." : "Send to Client"}
            </button>
          )}
          <button
            onClick={copyShareLink}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50"
          >
            <LinkIcon className="h-4 w-4 mr-1" />
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Sailing Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Cruise Details</h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-xl font-bold text-gray-900">{quote.sailing.ship}</h3>
              <p className="text-gray-600 mt-1">
                {quote.sailing.cruiseLine} &bull; {quote.sailing.nights} Nights
              </p>
              <p className="text-gray-600">{quote.sailing.product}</p>
              <p className="text-gray-600 mt-1">
                <ClockIcon className="inline h-4 w-4 mr-1" />
                Departing {formatDate(quote.sailing.sailDate)}
              </p>
              {quote.sailing.region && (
                <p className="text-sm text-gray-500 mt-1">{quote.sailing.region}</p>
              )}
            </div>
          </div>

          {/* Cabin Options */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Cabin Options ({quote.cabinSelections.length})
            </h2>
            <div className="space-y-3">
              {quote.cabinSelections.map((cabin, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    quote.acceptedCabinIndex === idx
                      ? "border-green-500 bg-green-50"
                      : cabin.isRecommended
                      ? "border-yellow-300 bg-yellow-50"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{cabin.categoryName}</span>
                        {cabin.isRecommended && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                            Recommended
                          </span>
                        )}
                        {quote.acceptedCabinIndex === idx && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center">
                            <CheckCircleIcon className="h-3 w-3 mr-1" /> Accepted
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Category: {cabin.category} &bull; {cabin.numGuests} guest{cabin.numGuests !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(cabin.pricePerPerson, cabin.currency)}
                      </p>
                      <p className="text-xs text-gray-500">per person</p>
                      {cabin.savingsPercent > 0 && (
                        <p className="text-sm text-green-600 font-medium">
                          Save {cabin.savingsPercent}%
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mt-1">
                        Total: {formatCurrency(cabin.totalPrice || cabin.pricePerPerson * cabin.numGuests, cabin.currency)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Messages */}
          {(quote.customMessage || quote.notes) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {quote.customMessage && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-1">Personal Message</h3>
                  <p className="text-gray-700">{quote.customMessage}</p>
                </div>
              )}
              {quote.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-1">Notes</h3>
                  <p className="text-gray-700">{quote.notes}</p>
                </div>
              )}
            </div>
          )}

          {quote.internalNotes && (
            <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-6">
              <h3 className="text-sm font-semibold text-yellow-700 uppercase mb-1">Internal Notes</h3>
              <p className="text-yellow-800">{quote.internalNotes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Client Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Client</h2>
            <p className="font-medium text-gray-900">{quote.client.firstName} {quote.client.lastName}</p>
            <p className="text-sm text-gray-600">{quote.client.email}</p>
            {quote.client.phone && <p className="text-sm text-gray-600">{quote.client.phone}</p>}
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Timeline</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">{formatDate(quote.createdAt)}</span>
              </div>
              {quote.sentAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Sent</span>
                  <span className="text-gray-900">{formatDate(quote.sentAt)}</span>
                </div>
              )}
              {quote.lastViewedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Viewed</span>
                  <span className="text-gray-900">{formatRelativeDate(quote.lastViewedAt)}</span>
                </div>
              )}
              {quote.acceptedAt && (
                <div className="flex justify-between">
                  <span className="text-green-600">Accepted</span>
                  <span className="text-green-700 font-medium">{formatDate(quote.acceptedAt)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Expires</span>
                <span className={`${new Date(quote.expiresAt) < new Date() ? "text-red-600" : "text-gray-900"}`}>
                  {formatDate(quote.expiresAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Summary */}
          <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-6">
            <h2 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-2">Lowest Price</h2>
            <p className="text-3xl font-bold text-indigo-900">
              {formatCurrency(lowestPrice, currency)}
            </p>
            <p className="text-sm text-indigo-600">per person</p>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
            <Link
              to={`/quotes/${id}/edit`}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <PencilSquareIcon className="h-4 w-4" /> Edit Quote
            </Link>
            <button
              onClick={handleDuplicate}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <DocumentDuplicateIcon className="h-4 w-4" /> Duplicate
            </button>
            <button
              onClick={handleDelete}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4" /> Delete Quote
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
